import React from 'react';
import { usePasswordToggle } from '../../hooks/usePasswordToggle';

function BasicInfoStep({
  formData, onChange, onPhoneChange, validationErrors,
  onUsernameCheck, usernameChecked,
  onEmailCheck, emailChecked,
  onMobileCheck, mobileChecked,
  onAddressSearch
}) {
  const { inputType: pwType, toggle: togglePw, icon: pwIcon } = usePasswordToggle();
  const { inputType: pwConfirmType, toggle: togglePwConfirm, icon: pwConfirmIcon } = usePasswordToggle();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-gray-900 dark:text-white">기본정보</h3>
        <span className="text-xs text-primary-strong font-bold">* 필수입력사항</span>
      </div>

      <div className="space-y-4">
        {/* 아이디 */}
        <div className="group">
          <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5">아이디 *</label>
          <div className="flex gap-2">
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={onChange}
              className={`flex-1 bg-gray-50 dark:bg-[#0d1b12] border ${validationErrors.username ? 'border-red-500' : 'border-gray-200 dark:border-[#1d3324]'} text-gray-900 dark:text-white text-sm rounded-xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
              placeholder="4-20자의 아이디"
            />
            <button
              type="button"
              onClick={onUsernameCheck}
              className="bg-gray-900 dark:bg-gray-700 text-white px-4 rounded-xl text-xs font-bold hover:bg-black transition-colors whitespace-nowrap"
            >
              중복확인
            </button>
          </div>
          {validationErrors.username && <p className="text-red-500 text-xs mt-1.5 font-medium">{validationErrors.username}</p>}
          {usernameChecked && <p className="text-green-500 text-xs mt-1.5 font-bold">✓ 사용 가능한 아이디입니다</p>}
        </div>

        {/* 비밀번호 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="group">
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5">비밀번호 *</label>
            <div className="relative">
              <input
                type={pwType}
                name="password"
                value={formData.password}
                onChange={onChange}
                className={`w-full bg-gray-50 dark:bg-[#0d1b12] border ${validationErrors.password ? 'border-red-500' : 'border-gray-200 dark:border-[#1d3324]'} text-gray-900 dark:text-white text-sm rounded-xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all pr-10`}
                placeholder="최소 6자 이상"
              />
              <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer" onClick={togglePw} tabIndex="-1">
                <span className="material-symbols-outlined text-xl">{pwIcon}</span>
              </button>
            </div>
            {validationErrors.password && <p className="text-red-500 text-xs mt-1.5 font-medium">{validationErrors.password}</p>}
          </div>

          <div className="group">
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5">비밀번호 확인 *</label>
            <div className="relative">
              <input
                type={pwConfirmType}
                name="passwordConfirm"
                value={formData.passwordConfirm}
                onChange={onChange}
                className={`w-full bg-gray-50 dark:bg-[#0d1b12] border ${validationErrors.passwordConfirm ? 'border-red-500' : 'border-gray-200 dark:border-[#1d3324]'} text-gray-900 dark:text-white text-sm rounded-xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all pr-10`}
                placeholder="비밀번호 재입력"
              />
              <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer" onClick={togglePwConfirm} tabIndex="-1">
                <span className="material-symbols-outlined text-xl">{pwConfirmIcon}</span>
              </button>
            </div>
            {validationErrors.passwordConfirm && <p className="text-red-500 text-xs mt-1.5 font-medium">{validationErrors.passwordConfirm}</p>}
            {formData.passwordConfirm && formData.password === formData.passwordConfirm && <p className="text-green-500 text-xs mt-1.5 font-bold">✓ 비밀번호가 일치합니다</p>}
          </div>
        </div>

        {/* 이름 */}
        <div className="group">
          <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5">이름 *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={onChange}
            className={`w-full bg-gray-50 dark:bg-[#0d1b12] border ${validationErrors.name ? 'border-red-500' : 'border-gray-200 dark:border-[#1d3324]'} text-gray-900 dark:text-white text-sm rounded-xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
            placeholder="이름 입력"
          />
          {validationErrors.name && <p className="text-red-500 text-xs mt-1.5 font-medium">{validationErrors.name}</p>}
        </div>

        {/* 전화번호 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="group">
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5">휴대폰번호 *</label>
            <div className="flex gap-2">
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={onPhoneChange}
                maxLength="13"
                className={`flex-1 bg-gray-50 dark:bg-[#0d1b12] border ${validationErrors.mobile ? 'border-red-500' : 'border-gray-200 dark:border-[#1d3324]'} text-gray-900 dark:text-white text-sm rounded-xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
                placeholder="010-0000-0000"
              />
              {onMobileCheck && (
                <button
                  type="button"
                  onClick={onMobileCheck}
                  className="px-4 py-3.5 bg-gray-800 text-white text-xs font-bold rounded-xl hover:bg-black whitespace-nowrap"
                >
                  중복확인
                </button>
              )}
            </div>
            {validationErrors.mobile && <p className="text-red-500 text-xs mt-1.5 font-medium">{validationErrors.mobile}</p>}
            {mobileChecked && <p className="text-green-500 text-xs mt-1.5 font-bold">✓ 사용 가능한 휴대폰 번호입니다</p>}
          </div>
          <div className="group">
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5">전화번호</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={onPhoneChange}
              maxLength="13"
              className="w-full bg-gray-50 dark:bg-[#0d1b12] border border-gray-200 dark:border-[#1d3324] text-gray-900 dark:text-white text-sm rounded-xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              placeholder="선택사항"
            />
          </div>
        </div>

        {/* 주소 */}
        <div className="group">
          <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5">주소</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              name="zipcode"
              value={formData.zipcode}
              readOnly
              className="w-24 bg-gray-100 dark:bg-[#1d3324] border border-gray-200 dark:border-[#1d3324] text-gray-500 dark:text-gray-300 text-sm rounded-xl px-4 py-3.5"
              placeholder="우편번호"
            />
            <button
              type="button"
              onClick={onAddressSearch}
              className="bg-white border border-gray-200 text-gray-700 px-4 rounded-xl text-xs font-bold hover:bg-gray-50 transition-colors"
            >
              주소검색
            </button>
          </div>
          <input
            type="text"
            name="address"
            value={formData.address}
            readOnly
            className="w-full bg-gray-100 dark:bg-[#1d3324] border border-gray-200 dark:border-[#1d3324] text-gray-500 dark:text-gray-300 text-sm rounded-xl px-4 py-3.5 mb-2"
            placeholder="기본 주소"
          />
          <input
            type="text"
            name="addressDetail"
            value={formData.addressDetail}
            onChange={onChange}
            className="w-full bg-gray-50 dark:bg-[#0d1b12] border border-gray-200 dark:border-[#1d3324] text-gray-900 dark:text-white text-sm rounded-xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            placeholder="상세 주소를 입력하세요"
          />
        </div>

        {/* 이메일 */}
        <div className="group">
          <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5">이메일 *</label>
          <div className="flex gap-2">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={onChange}
              className={`flex-1 bg-gray-50 dark:bg-[#0d1b12] border ${validationErrors.email ? 'border-red-500' : 'border-gray-200 dark:border-[#1d3324]'} text-gray-900 dark:text-white text-sm rounded-xl px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all`}
              placeholder="example@email.com"
            />
            <button
              type="button"
              onClick={onEmailCheck}
              className="bg-gray-900 dark:bg-gray-700 text-white px-4 rounded-xl text-xs font-bold hover:bg-black transition-colors whitespace-nowrap"
            >
              중복확인
            </button>
          </div>
          {validationErrors.email && <p className="text-red-500 text-xs mt-1.5 font-medium">{validationErrors.email}</p>}
          {emailChecked && <p className="text-green-500 text-xs mt-1.5 font-bold">✓ 사용 가능한 이메일입니다</p>}
        </div>
      </div>
    </div>
  );
}

export default BasicInfoStep;
